import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { API } from "@/App";
import { toast } from "sonner";
import { ArrowLeft, Pencil, Check, X } from "lucide-react";
import { CadiiChat } from "@/components/CadiiChat";
import { SocialCreateButton } from "@/pages/SocialStudio";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const MODULE_NAMES = {
  nassau: "Nassau", skins: "Skins", match_play: "Match Play", units: "Unidades", oyes: "Oyes", foursome: "Foursome",
};

export default function PlayResults() {
  const { roundId } = useParams();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editHole, setEditHole] = useState(null);
  const [editScores, setEditScores] = useState({});
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API}/v2/rounds/${roundId}/summary`, { credentials: "include" });
      if (!res.ok) throw new Error();
      setSummary(await res.json());
    } catch {
      toast.error("Ronda no encontrada");
      navigate("/dashboard");
    } finally {
      setLoading(false);
    }
  }, [roundId, navigate]);

  useEffect(() => { load(); }, [load]);

  if (loading || !summary) {
    return <div className="min-h-screen flex items-center justify-center bg-[#F7F3EA]"><div className="w-8 h-8 border-3 border-[#7ED957] border-t-transparent rounded-full animate-spin" /></div>;
  }

  const players = summary.players || [];
  const scorecard = summary.scorecard || [];
  const holeScores = summary.hole_scores || {};
  const courseHoles = summary.course?.holes || [];
  const canEdit = summary.is_owner;
  const nameOf = (pid) => players.find(p => p.player_id === pid)?.name || "—";

  const openEdit = (hole) => {
    if (!canEdit) return;
    const existing = holeScores[String(hole)] || {};
    const initial = {};
    players.forEach(p => { initial[p.player_id] = existing[p.player_id] ?? ""; });
    setEditScores(initial);
    setEditHole(hole);
  };

  const saveEdit = async () => {
    const scores = Object.entries(editScores)
      .filter(([, v]) => v !== "" && parseInt(v) > 0)
      .map(([pid, v]) => ({ player_id: pid, strokes: parseInt(v) }));
    if (scores.length === 0) { toast.error("Ingresa al menos un score"); return; }
    setSaving(true);
    try {
      const res = await fetch(`${API}/v2/rounds/${roundId}/scores`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ hole: editHole, scores }),
      });
      if (!res.ok) throw new Error();
      toast.success(`Hoyo ${editHole} corregido`);
      setEditHole(null);
      await load();
    } catch {
      toast.error("Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const scoreColor = (strokes, par) => {
    if (!strokes) return "text-[#C4CDC5]";
    const d = strokes - par;
    if (d <= -2) return "text-amber-500 font-black";
    if (d === -1) return "text-blue-600 font-bold";
    if (d === 0) return "text-[#102E32]";
    if (d === 1) return "text-amber-600";
    return "text-red-600";
  };

  const sorted = [...scorecard].sort((a, b) => (a.total || 999) - (b.total || 999));
  const holesCount = summary.holes_count || 18;
  const startingHole = summary.starting_hole || 1;
  const showFront = holesCount === 18 || startingHole === 1;
  const showBack = holesCount === 18 || startingHole === 10;
  const totalPar = courseHoles.reduce((s, h) => {
    if (holesCount === 9 && ((startingHole === 1 && h.hole > 9) || (startingHole === 10 && h.hole < 10))) return s;
    return s + h.par;
  }, 0);

  const HoleTable = ({ from, to, label, totalKey }) => {
    const holes = Array.from({ length: to - from + 1 }, (_, i) => from + i);
    return (
      <div className="overflow-x-auto">
        <p className="text-[10px] uppercase tracking-widest text-[#ADB8AE] mb-1.5">{label}</p>
        <table className="w-full text-center text-sm">
          <thead>
            <tr className="text-[#9AA69C] text-[10px]">
              <th className="text-left py-1 pr-2 font-medium min-w-[80px]">Hoyo</th>
              {holes.map(h => (
                <th key={h} className="px-1 py-1 font-bold">
                  {canEdit ? (
                    <button onClick={() => openEdit(h)} className="w-7 h-7 rounded-md bg-[#F1EBDD] hover:bg-[#103B31] transition-colors flex items-center justify-center mx-auto" data-testid={`edit-hole-${h}`}>
                      {h}
                    </button>
                  ) : h}
                </th>
              ))}
              <th className="px-1.5 py-1 font-bold text-[#6B7A6F]">TOT</th>
            </tr>
            <tr className="text-[#B8C2B9] text-[9px]">
              <td className="text-left pr-2">Par</td>
              {holes.map(h => <td key={h}>{courseHoles.find(c => c.hole === h)?.par || "-"}</td>)}
              <td className="font-bold">{holes.reduce((s, h) => s + (courseHoles.find(c => c.hole === h)?.par || 0), 0)}</td>
            </tr>
          </thead>
          <tbody>
            {scorecard.map(p => {
              const arr = from === 1 ? p.front_9 : p.back_9;
              return (
                <tr key={p.player_id} className="border-t border-white/5">
                  <td className="text-left py-1.5 pr-2 text-[#4B5A50] font-medium truncate max-w-[90px]">{p.name}</td>
                  {holes.map((h, i) => {
                    const par = courseHoles.find(c => c.hole === h)?.par || 4;
                    return (
                      <td key={h} className={`py-1.5 ${scoreColor(arr[i], par)} ${canEdit ? "cursor-pointer" : ""}`}
                        onClick={() => openEdit(h)}>
                        {arr[i] || "·"}
                      </td>
                    );
                  })}
                  <td className="py-1.5 font-black text-[#102E32]">{p[totalKey] || "—"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#F7F3EA] text-[#102E32] pb-12" style={{ fontFamily: "Manrope, sans-serif" }} data-testid="play-results-page">
      {/* Top bar */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-[#DED5C2] bg-white/60">
        <button onClick={() => navigate(summary.status === "completed" ? "/dashboard" : `/play/${roundId}`)} className="text-[#6B7A6F] hover:text-[#102E32]" data-testid="back-btn"><ArrowLeft size={20} /></button>
        <span className="text-sm font-medium text-[#3D4A42] truncate max-w-[220px]">{summary.name}</span>
        <div className="flex items-center gap-2">
          {summary.doubled_back9 && (
            <span className="text-[10px] font-black text-[#103B31] bg-[#103B31]/10 px-2 py-0.5 rounded-full" data-testid="results-doubled-badge">x2 Vuelta</span>
          )}
          <span className="text-xs text-[#9AA69C]">{summary.date || ""}</span>
        </div>
      </div>

      {/* Podium */}
      <div className="text-center py-8 px-4">
        <img src="/cadii/mascots/golfball_celebrate.png" alt="" className="w-24 h-24 object-contain mx-auto mb-2" />
        <p className="text-xs uppercase tracking-widest text-[#9AA69C] mb-1">{summary.status === "completed" ? "Resultado Final" : "Resultado Parcial"}</p>
        {sorted[0]?.total > 0 && (
          <>
            <p className="text-4xl font-black" data-testid="winner-name">{sorted[0].name}</p>
            <p className="text-lg text-[#103B31] font-bold mt-1">
              {sorted[0].total} golpes
              {totalPar > 0 && summary.status === "completed" && (
                <span className="text-[#9AA69C] font-medium"> ({sorted[0].total - totalPar > 0 ? "+" : ""}{sorted[0].total - totalPar})</span>
              )}
            </p>
          </>
        )}
        {summary.status !== "completed" && (
          <button onClick={() => navigate(`/play/${roundId}`)}
            className="mt-4 px-6 py-2.5 rounded-full bg-[#103B31] text-white text-sm font-bold hover:bg-[#092B25] transition-colors"
            data-testid="continue-playing-btn">
            Seguir jugando
          </button>
        )}
      </div>

      {/* Social Studio */}
      <div className="px-4 mb-6">
        <div className="bg-[#103B31] rounded-2xl p-4 shadow-sm">
          <p className="text-white font-black text-lg">Comparte tu ronda</p>
          <p className="text-white/70 text-xs mb-3">Crea contenido con tus datos en segundos</p>
          <SocialCreateButton roundId={roundId} type="round" template="round_recap_01" />
        </div>
      </div>

      {/* Standings */}
      <div className="px-4 mb-6">
        <div className="bg-white rounded-2xl border border-[#DED5C2] shadow-sm p-4 space-y-2">
          {sorted.map((p, i) => (
            <div key={p.player_id} className="flex items-center gap-3" data-testid={`standing-${p.player_id}`}>
              <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-black ${i === 0 ? "bg-yellow-400 text-black" : "bg-[#F1EBDD] text-[#6B7A6F]"}`}>{i + 1}</span>
              <span className="flex-1 font-semibold text-[#22302A] truncate">{p.name}</span>
              <span className="text-2xl font-black">{p.total || "—"}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Scorecard editable */}
      <div className="px-4 mb-6">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-bold text-[#22302A]">Tarjeta de Scores</h3>
          {canEdit && (
            <span className="flex items-center gap-1 text-[10px] text-[#103B31] bg-[#103B31]/10 px-2 py-1 rounded-full" data-testid="edit-hint">
              <Pencil size={10} /> Toca un hoyo para corregir
            </span>
          )}
        </div>
        <div className="bg-white rounded-2xl border border-[#DED5C2] shadow-sm p-4 space-y-5">
          {showFront && <HoleTable from={1} to={9} label={holesCount === 9 ? "Ida" : "Ida (1-9)"} totalKey="front_total" />}
          {showBack && <HoleTable from={10} to={18} label={holesCount === 9 ? "Vuelta" : "Vuelta (10-18)"} totalKey="back_total" />}
        </div>
      </div>

      {/* Module summaries */}
      {Object.keys(summary.module_summaries || {}).length > 0 && (
        <div className="px-4 space-y-3">
          <h3 className="font-bold text-[#22302A]">Juegos y Apuestas</h3>
          {Object.entries(summary.module_summaries).map(([modId, s]) => (
            <div key={modId} className="bg-white rounded-2xl border border-[#DED5C2] shadow-sm p-4" data-testid={`module-summary-${modId}`}>
              <div className="flex items-center justify-between mb-2">
                <p className="font-bold text-sm">{MODULE_NAMES[modId] || modId}</p>
                {s.amount > 0 && <span className="text-xs text-[#103B31] font-bold">${s.amount} MXN</span>}
              </div>
              <ModuleSummary modId={modId} s={s} nameOf={nameOf} />
            </div>
          ))}
        </div>
      )}

      {/* Edit dialog */}
      <Dialog open={editHole !== null} onOpenChange={(o) => !o && setEditHole(null)}>
        <DialogContent className="bg-white border-[#DED5C2] text-[#102E32] max-w-sm" style={{ fontFamily: "Manrope, sans-serif" }}>
          <DialogHeader>
            <DialogTitle className="text-center">
              Corregir Hoyo {editHole}
            </DialogTitle>
            <DialogDescription className="text-center text-xs text-[#9AA69C]">
              Par {courseHoles.find(c => c.hole === editHole)?.par || "-"} · Ajusta los scores y guarda
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {players.map(p => (
              <div key={p.player_id} className="flex items-center gap-3">
                <span className="flex-1 text-sm font-semibold truncate">{p.name}</span>
                <button onClick={() => setEditScores(prev => ({ ...prev, [p.player_id]: Math.max(1, (parseInt(prev[p.player_id]) || (courseHoles.find(c => c.hole === editHole)?.par || 4)) - 1) }))}
                  className="w-11 h-11 rounded-xl bg-[#F1EBDD] text-[#102E32] text-xl font-bold active:bg-[#E9E1CE]">−</button>
                <input type="number" inputMode="numeric" value={editScores[p.player_id] ?? ""}
                  onChange={e => setEditScores(prev => ({ ...prev, [p.player_id]: e.target.value === "" ? "" : parseInt(e.target.value) || 0 }))}
                  className="w-16 h-11 bg-white border border-[#DED5C2] rounded-xl text-center text-2xl font-black focus:outline-none focus:ring-2 focus:ring-[#7ED957]"
                  data-testid={`edit-score-${p.player_id}`} />
                <button onClick={() => setEditScores(prev => ({ ...prev, [p.player_id]: (parseInt(prev[p.player_id]) || (courseHoles.find(c => c.hole === editHole)?.par || 4) - 1) + 1 }))}
                  className="w-11 h-11 rounded-xl bg-[#F1EBDD] text-[#102E32] text-xl font-bold active:bg-[#E9E1CE]" aria-label="Aumentar score">+</button>
              </div>
            ))}
          </div>
          <div className="flex gap-2 mt-2">
            <button onClick={() => setEditHole(null)}
              className="flex-1 py-3 rounded-xl bg-[#F1EBDD] text-[#4B5A50] font-semibold flex items-center justify-center gap-1.5">
              <X size={16} /> Cancelar
            </button>
            <button onClick={saveEdit} disabled={saving}
              className="flex-1 py-3 rounded-xl bg-[#103B31] hover:bg-[#092B25] font-bold flex items-center justify-center gap-1.5 disabled:opacity-50"
              data-testid="save-edit-btn">
              <Check size={16} /> {saving ? "Guardando..." : "Guardar"}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <CadiiChat roundId={roundId} />
    </div>
  );
}

const Row = ({ label, value }) => (
  <div className="flex items-center justify-between text-sm py-0.5">
    <span className="text-[#6B7A6F]">{label}</span>
    <span className="font-bold text-[#22302A]">{value}</span>
  </div>
);

const ModuleSummary = ({ modId, s, nameOf }) => {
  if (modId === "nassau") {
    const seg = (label, data) => (
      <Row label={label} value={data?.winner ? (data.winner === "tie" ? "Empate" : nameOf(data.winner)) : "Pendiente"} />
    );
    return <div>{seg("Ida (Front 9)", s.front_9)}{seg("Vuelta (Back 9)", s.back_9)}{seg("Total", s.total)}</div>;
  }
  if (modId === "skins") {
    return (
      <div>
        {Object.entries(s.skins_won || {}).map(([pid, n]) => <Row key={pid} label={nameOf(pid)} value={`${n} skins${s.amount > 0 ? ` · $${n * s.amount}` : ""}`} />)}
        {s.pending_carry > 0 && <Row label="Acumulado sin ganar" value={`${s.pending_carry}`} />}
      </div>
    );
  }
  if (modId === "match_play") {
    return (
      <div>
        {Object.entries(s.holes_won || {}).map(([pid, n]) => <Row key={pid} label={nameOf(pid)} value={`${n} pts${s.amount > 0 ? ` · $${n * s.amount}` : ""}`} />)}
        <Row label="Empates" value={s.ties} />
        {s.leader && <Row label="Líder" value={`${nameOf(s.leader)} (+${s.lead})`} />}
      </div>
    );
  }
  if (modId === "units") {
    return <div>{Object.entries(s.totals || {}).map(([pid, n]) => <Row key={pid} label={nameOf(pid)} value={`${n > 0 ? "+" : ""}${n}${s.amount > 0 ? ` · $${n * s.amount}` : ""}`} />)}</div>;
  }
  if (modId === "oyes") {
    const gs = s.grand_slam;
    return (
      <div>
        {Object.entries(s.count || {}).map(([pid, n]) => {
          const bonus = gs?.active && gs.player_id === pid ? gs.bonus : 0;
          return <Row key={pid} label={nameOf(pid)} value={`${n} oyes${bonus ? ` +${bonus} GS` : ""}${s.amount > 0 ? ` · $${(n + bonus) * s.amount}` : ""}`} />;
        })}
        {gs?.active && (
          <div className="mt-2 flex items-center justify-between text-sm bg-yellow-400/10 rounded-lg px-2.5 py-1.5" data-testid="grand-slam-row">
            <span className="text-amber-500 font-black">GRAND SLAM</span>
            <span className="font-bold text-amber-500">{nameOf(gs.player_id)} · +{gs.bonus} oyes</span>
          </div>
        )}
      </div>
    );
  }
  return null;
};
